<template>
  <v-dialog
    :value="value"
    persistent
    scrollable
    max-width="1200"
    content-class="wl-add-dialog"
    @input="$emit('input', $event)"
  >
    <v-card class="wl-add">
      <div class="wl-add__hero">
        <div class="wl-add__hero-main">
          <div class="wl-add__hero-icon">
            <v-icon color="white">mdi-shield-plus-outline</v-icon>
          </div>
          <div>
            <div class="wl-add__hero-title">Добавить в белый список</div>
            <div class="wl-add__hero-text">
              Отберите домофоны, калитки и доп. входы — на них не будет рекламы и показаний счётчиков
            </div>
          </div>
        </div>
        <v-btn
          icon
          class="wl-add__close"
          aria-label="Закрыть"
          @click="close"
        >
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="wl-add__body">
        <v-row>
          <v-col cols="12" md="3" class="pb-2 pt-4">
            <div class="wl-add__filters">
              <div class="wl-add__filters-head">
                <div class="wl-add__filters-icon">
                  <v-icon color="white" small>mdi-filter-variant</v-icon>
                </div>
                <div>
                  <div class="wl-add__filters-title">Фильтры</div>
                  <div class="wl-add__filters-sub">Сужайте выбор устройств</div>
                </div>
              </div>
              <v-text-field
                v-model="filters.id"
                label="ID домофона"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-identifier"
                class="mb-3"
                @keyup.enter="onSearch"
              />
              <v-text-field
                v-model="filters.address"
                label="Адрес"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="mb-3"
                @keyup.enter="onSearch"
              />
              <v-autocomplete
                v-model="filters.region_id"
                label="Район"
                :items="regions"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details
                clearable
                multiple
                small-chips
                deletable-chips
                prepend-inner-icon="mdi-home-city-outline"
                class="mb-3"
              />
              <v-autocomplete
                v-model="filters.management_company_id"
                label="УК"
                :items="managementCompanies"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-home-account"
                class="mb-3"
              />
              <v-autocomplete
                v-model="filters.agent_id"
                label="Агент"
                :items="agents"
                item-text="user.name"
                item-value="id"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-account-search"
                class="mb-4"
              />
              <v-btn
                color="primary"
                class="text-none wl-add__btn wl-add__btn--search mb-2"
                block
                rounded
                elevation="0"
                @click="onSearch"
              >
                <v-icon left small>mdi-magnify</v-icon>
                Найти
              </v-btn>
              <v-btn
                class="text-none wl-add__btn wl-add__btn--ghost"
                block
                rounded
                outlined
                color="primary"
                @click="resetFilters"
              >
                <v-icon left small>mdi-filter-off-outline</v-icon>
                Сбросить
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="9" class="pb-2">
            <div class="wl-add__toolbar pt-2">
              <div
                class="wl-add__counter"
                :class="{ 'wl-add__counter--active': selected.length }"
              >
                <v-icon left small :color="selected.length ? 'white' : 'primary'">mdi-check-circle-outline</v-icon>
                Выбрано: {{ selected.length }}
                <span class="wl-add__counter-extra">
                  · на странице {{ pageSelected.length }} · всего {{ totalDoorphones }}
                </span>
              </div>
              <div class="wl-add__actions">
                <v-btn
                  small
                  rounded
                  outlined
                  color="primary"
                  class="text-none wl-add__btn"
                  :loading="selectingAll"
                  :disabled="loading || !totalDoorphones"
                  @click="selectAllByFilter"
                >
                  <v-icon left x-small>mdi-checkbox-multiple-marked-outline</v-icon>
                  Все по фильтру
                </v-btn>
                <v-btn
                  small
                  rounded
                  outlined
                  class="text-none wl-add__btn"
                  :disabled="!selected.length"
                  @click="clearSelection"
                >
                  <v-icon left x-small>mdi-close-circle-outline</v-icon>
                  Снять выбор
                </v-btn>
              </div>
            </div>

            <div class="wl-add__pager">
              <span class="wl-add__pager-label">Строк</span>
              <v-select
                :value="options.itemsPerPage"
                :items="pageSizeOptions"
                dense
                hide-details
                outlined
                class="wl-add__pager-select"
                @change="setItemsPerPage"
              />
              <span class="wl-add__range">{{ pageRangeText }}</span>
              <v-btn icon x-small :disabled="options.page <= 1 || loading" @click="setPage(1)">
                <v-icon small>mdi-page-first</v-icon>
              </v-btn>
              <v-btn icon x-small :disabled="options.page <= 1 || loading" @click="setPage(options.page - 1)">
                <v-icon small>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn icon x-small :disabled="options.page >= pageCount || loading" @click="setPage(options.page + 1)">
                <v-icon small>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn icon x-small :disabled="options.page >= pageCount || loading" @click="setPage(pageCount)">
                <v-icon small>mdi-page-last</v-icon>
              </v-btn>
            </div>

            <mass-open-door-sound-list
              :items="doorphones"
              :selected.sync="pageSelected"
              :total="totalDoorphones"
              :options.sync="options"
              :loading="loading"
              hide-region
              :sortable="false"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="wl-add__footer">
        <v-text-field
          v-model="comment"
          label="Комментарий ко всем выбранным"
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-comment-text-outline"
          class="wl-add__comment"
          maxlength="500"
        />
        <v-spacer />
        <v-btn
          rounded
          outlined
          class="text-none wl-add__btn"
          @click="close"
        >
          Отмена
        </v-btn>
        <v-btn
          rounded
          elevation="0"
          class="text-none wl-add__btn wl-add__btn--submit"
          :disabled="!selected.length"
          :loading="saving"
          @click="submit"
        >
          <v-icon left small>mdi-shield-plus-outline</v-icon>
          {{ selected.length ? `Добавить ${selected.length}` : 'Добавить' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import MassOpenDoorSoundList from '@/components/massOpenDoorSound/MassOpenDoorSoundList.vue';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const MAX_COUNT = 1000;

function emptyFilters () {
  return {
    id: '',
    address: '',
    region_id: [],
    management_company_id: null,
    agent_id: null,
  };
}

export default {
  name: 'MassOpenDoorSoundWhitelistAddDialog',
  components: {
    MassOpenDoorSoundList,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      filters: emptyFilters(),
      doorphones: [],
      totalDoorphones: 0,
      loading: false,
      selectingAll: false,
      selectedMap: {},
      pageSelected: [],
      suppressSelectionSync: false,
      suppressOptionsWatch: false,
      comment: '',
      options: {
        page: 1,
        itemsPerPage: 20,
      },
      pageSizeOptions: PAGE_SIZE_OPTIONS,
    };
  },
  computed: {
    ...mapGetters({
      managementCompanies: 'ManagementCompany/managementCompanies',
      agents: 'Agents/agents',
      regions: 'Region/regions',
    }),
    selected () {
      return Object.values(this.selectedMap);
    },
    pageCount () {
      const perPage = Number(this.options.itemsPerPage) || 20;
      return Math.max(1, Math.ceil((this.totalDoorphones || 0) / perPage));
    },
    pageRangeText () {
      if (!this.totalDoorphones) return '0 из 0';
      const perPage = Number(this.options.itemsPerPage) || 20;
      const from = (this.options.page - 1) * perPage + 1;
      const to = Math.min(this.totalDoorphones, this.options.page * perPage);
      return `${from}–${to} из ${this.totalDoorphones}`;
    },
    apiFilters () {
      const f = {
        open_door_sound_supported: 1,
        exclude_open_door_sound_whitelist: 1,
      };
      const ids = this.parseIdFilter(this.filters.id);
      if (ids.length) f.id = ids;
      if (this.filters.address) f.address = this.filters.address;
      if (this.filters.management_company_id) f.management_company_id = this.filters.management_company_id;
      if (this.filters.agent_id) f.agent_id = this.filters.agent_id;
      if (this.filters.region_id && this.filters.region_id.length) f.region_id = this.filters.region_id;
      return f;
    },
  },
  watch: {
    value (open) {
      if (open) {
        this.resetState();
        this.loadDictionaries();
        this.loadDoorphones();
      }
    },
    options: {
      deep: true,
      handler () {
        if (!this.value || this.suppressOptionsWatch) return;
        this.loadDoorphones();
      },
    },
    pageSelected (rows) {
      if (this.suppressSelectionSync) return;
      const pageIds = new Set(this.doorphones.map((d) => d.id));
      const selectedOnPage = new Set((rows || []).map((r) => r.id));

      Object.keys(this.selectedMap).forEach((id) => {
        const numId = Number(id);
        if (pageIds.has(numId) && !selectedOnPage.has(numId)) {
          this.$delete(this.selectedMap, id);
        }
      });

      (rows || []).forEach((row) => {
        if (!this.selectedMap[row.id]) {
          this.$set(this.selectedMap, row.id, row);
        }
      });
    },
  },
  methods: {
    ...mapActions({
      getDoorphones: 'Doorphone/getMfDoorphones',
      loadManagementCompanies: 'ManagementCompany/getManagementCompanies',
      loadRegions: 'Region/getRegions',
      loadAllAgents: 'Agents/loadAllAgents',
    }),
    parseIdFilter (value) {
      return String(value || '')
        .split(/[\s,;]+/)
        .map((part) => Number(part))
        .filter((id) => Number.isInteger(id) && id > 0);
    },
    loadDictionaries () {
      this.loadManagementCompanies({ filters: { with_doorphones: 1 } });
      this.loadRegions();
      this.loadAllAgents();
    },
    resetState () {
      this.suppressOptionsWatch = true;
      this.suppressSelectionSync = true;
      this.filters = emptyFilters();
      this.selectedMap = {};
      this.pageSelected = [];
      this.comment = '';
      this.options = { page: 1, itemsPerPage: 20 };
      this.doorphones = [];
      this.totalDoorphones = 0;
      this.$nextTick(() => {
        this.suppressOptionsWatch = false;
        this.suppressSelectionSync = false;
      });
    },
    close () {
      this.$emit('input', false);
    },
    onSearch () {
      if (this.options.page !== 1) {
        this.options.page = 1;
        return;
      }
      this.loadDoorphones();
    },
    resetFilters () {
      this.filters = emptyFilters();
      this.onSearch();
    },
    setPage (page) {
      this.options.page = page;
    },
    setItemsPerPage (value) {
      this.suppressOptionsWatch = true;
      this.options.itemsPerPage = Number(value) || 20;
      this.options.page = 1;
      this.$nextTick(() => {
        this.suppressOptionsWatch = false;
        this.loadDoorphones();
      });
    },
    loadDoorphones () {
      this.loading = true;
      this.getDoorphones({
        page: this.options.page,
        limit: this.options.itemsPerPage,
        filters: this.apiFilters,
      })
        .then((response) => {
          this.suppressSelectionSync = true;
          this.doorphones = response.doorphones || [];
          this.totalDoorphones = response.meta?.total || 0;
          this.pageSelected = this.doorphones.filter((d) => this.selectedMap[d.id]);
          this.$nextTick(() => {
            this.suppressSelectionSync = false;
          });
        })
        .catch(() => {
          this.$flash_warning('Ошибка при загрузке домофонов');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clearSelection () {
      this.suppressSelectionSync = true;
      this.selectedMap = {};
      this.pageSelected = [];
      this.$nextTick(() => {
        this.suppressSelectionSync = false;
      });
    },
    selectAllByFilter () {
      this.selectingAll = true;
      this.getDoorphones({
        ids_only: 1,
        filters: this.apiFilters,
      })
        .then((response) => {
          const items = response.doorphones || [];
          if (response.truncated || items.length > MAX_COUNT) {
            this.$flash_warning(`По фильтру больше ${MAX_COUNT} домофонов. Выбраны первые ${MAX_COUNT}.`);
          }
          const next = {};
          items.slice(0, MAX_COUNT).forEach((item) => {
            next[item.id] = item;
          });
          this.selectedMap = next;
          this.suppressSelectionSync = true;
          this.pageSelected = this.doorphones.filter((d) => next[d.id]);
          this.$nextTick(() => {
            this.suppressSelectionSync = false;
          });
          if (!items.length) {
            this.$flash_warning('По текущим фильтрам нет домофонов');
          }
        })
        .catch(() => {
          this.$flash_warning('Не удалось выбрать домофоны по фильтру');
        })
        .finally(() => {
          this.selectingAll = false;
        });
    },
    submit () {
      const ids = this.selected.map((item) => item.id);
      if (!ids.length) return;
      this.$emit('submit', {
        doorphoneIds: ids,
        comment: this.comment || null,
      });
    },
  },
};
</script>

<style scoped>
.wl-add {
  overflow: hidden;
  border-radius: 16px !important;
}
.wl-add__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(135deg, #0d4092 0%, #1565c0 48%, #1e88e5 100%);
  color: #fff;
}
.wl-add__hero-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.wl-add__hero-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wl-add__hero-title {
  font-size: 1.12rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
}
.wl-add__hero-text {
  margin-top: 3px;
  font-size: 0.78rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.82);
  max-width: 560px;
}
.wl-add__close {
  background: rgba(255, 255, 255, 0.14) !important;
  flex-shrink: 0;
}
.wl-add__body {
  max-height: 64vh;
  background: linear-gradient(180deg, #f3f7fc 0%, #fff 120px);
  padding-top: 16px;
}
.wl-add__filters {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 14px;
  padding: 14px 12px;
  box-shadow: 0 2px 10px rgba(13, 64, 146, 0.05);
}
.wl-add__filters-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.wl-add__filters-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0d4092 0%, #1e88e5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wl-add__filters-title {
  font-weight: 650;
  font-size: 0.95rem;
  color: #1a237e;
  line-height: 1.2;
}
.wl-add__filters-sub {
  font-size: 0.72rem;
  color: #78909c;
}
.wl-add__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.wl-add__counter {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #fff;
  color: #0d4092;
  font-weight: 650;
  font-size: 0.82rem;
  border: 1px solid #d6e4f5;
  box-shadow: 0 1px 4px rgba(13, 64, 146, 0.06);
}
.wl-add__counter--active {
  background: linear-gradient(135deg, #0d4092 0%, #1e88e5 100%);
  color: #fff;
  border-color: transparent;
}
.wl-add__counter-extra {
  font-weight: 500;
  opacity: 0.78;
  margin-left: 4px;
}
.wl-add__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.wl-add__btn {
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  text-transform: none !important;
}
.wl-add__btn--search {
  height: 38px !important;
  box-shadow: 0 4px 12px rgba(13, 64, 146, 0.22) !important;
}
.wl-add__btn--ghost {
  height: 38px !important;
}
.wl-add__btn--submit {
  color: #fff !important;
  background: linear-gradient(135deg, #0d4092 0%, #1e88e5 100%) !important;
  box-shadow: 0 6px 16px rgba(13, 64, 146, 0.28) !important;
  padding-left: 18px !important;
  padding-right: 18px !important;
  min-height: 40px;
}
.wl-add__btn--submit.v-btn--disabled {
  background: #cfd8dc !important;
  box-shadow: none !important;
  color: #fff !important;
}
.wl-add__pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 6px;
}
.wl-add__pager-label,
.wl-add__range {
  font-size: 0.76rem;
  color: #90a4ae;
}
.wl-add__pager-select {
  max-width: 88px;
}
.wl-add__footer {
  padding: 12px 16px 16px;
  gap: 12px;
  border-top: 1px solid #e8eef5;
  background: #fafcfe;
}
.wl-add__comment {
  max-width: 420px;
}
</style>

<style>
.wl-add-dialog {
  overflow: hidden;
  border-radius: 16px !important;
  box-shadow: 0 18px 48px rgba(13, 64, 146, 0.22) !important;
}
</style>
